import { z } from 'zod';
import { IsoDateTime, Pence, Uuid } from '../primitives.js';

/**
 * WS10 — SaaS Billing & Subscriptions: Salonomy's OWN revenue (brief 10 §1; ADR-0010, ADR-0011).
 *
 * This is the PLATFORM-billing plane (salon → Salonomy), the structural inverse of the Stripe Connect
 * payments plane (salon → its client). It NEVER sets a Stripe-Account header, runs on the platform
 * Customer/Subscription/Invoice, and its tables are PLATFORM-scoped, NOT tenant-RLS (ADR-0010 §4):
 * isolation is enforced at the API surface — every billing route is owner-only and the salon_id ALWAYS
 * comes from the authenticated session/PAT, never a request parameter.
 *
 * FOUNDATION promise this proves: "No contracts, ever. Monthly rolling only, in-app one-click cancel,
 * itemised invoices." Plans/prices/knobs are DATA not code (ADR-0010 §2); the published numbers
 * (£15 Solo / £12 per chair / £249 Group, 14-day trial, etc.) are an engineering DEFAULT seed and
 * REMAIN A PROPOSAL — business/accountant/solicitor/DPO sign-off is required before launch.
 *
 * Money is integer pence; rates are basis points. Salonomy's own VAT (vatBps) is unrelated to a
 * salon's VAT settings used in the Connect/till flows (ADR-0010 §7).
 */

// ── Enums ────────────────────────────────────────────────────────────────────

/** Mirrors Stripe's subscription status; our DB is a reconciled cache (Stripe is source of truth). */
export const SubscriptionStatus = z.enum([
  'trialing',
  'active',
  'past_due',
  'unpaid',
  'canceled',
  'incomplete',
  'incomplete_expired',
  'paused',
]);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatus>;

/**
 * Salonomy's OWN derived write-gate state (ADR-0011 §3), distinct from Stripe `status`. read_only is a
 * Salonomy policy state, not a Stripe one: 'none' = full write access; 'dunning' = in the retry/grace
 * ladder, still fully usable; 'read_only' = new commerce frozen (diary visible, no new bookings).
 */
export const SubscriptionDelinquencyState = z.enum(['none', 'dunning', 'read_only']);
export type SubscriptionDelinquencyState = z.infer<typeof SubscriptionDelinquencyState>;

/** Subscription line component. 'comms_overage' is usage billed in arrears (auto_top_up salons only). */
export const SubscriptionItemKind = z.enum(['base', 'per_chair', 'comms_overage']);
export type SubscriptionItemKind = z.infer<typeof SubscriptionItemKind>;

export const InvoiceStatus = z.enum([
  'draft',
  'open',
  'paid',
  'uncollectible',
  'void',
  'payment_failed',
]);
export type InvoiceStatus = z.infer<typeof InvoiceStatus>;

/** The logged-usage source an invoice line drills to (the "why is this invoice this amount" trace). */
export const InvoiceLineKind = z.enum([
  'base',
  'per_chair',
  'comms_overage',
  'proration',
  'vat',
  'other',
]);
export type InvoiceLineKind = z.infer<typeof InvoiceLineKind>;

export const BillingEventStatus = z.enum(['received', 'processed', 'ignored', 'failed']);
export type BillingEventStatus = z.infer<typeof BillingEventStatus>;

// ── Plans (config, read-only via the API) ──────────────────────────────────────

/**
 * A billing plan as CONFIG (ADR-0010 §2). WS3's pricing page and plain-English terms read this at
 * build time so the published table and the billed amount cannot diverge. The numbers are a PROPOSAL.
 */
export const BillingPlan = z
  .object({
    id: Uuid,
    code: z.string().describe('stable plan code, e.g. solo | per_chair | group'),
    displayName: z.string(),
    basePricePence: Pence.describe('flat monthly component (0 for a pure per-chair plan)'),
    perChairPricePence: Pence.describe('multiplied by the derived billable-chair quantity'),
    currency: z.literal('GBP'),
    vatBps: z.int().min(0).max(10000).describe("Salonomy's own VAT in bps (2000 = 20.00%)"),
    includedSmsPerChair: z.int().min(0).describe('pooled comms allowance per chair'),
    trialPeriodDays: z.int().min(0),
    retryDays: z
      .array(z.int().min(0))
      .describe('dunning retry days from first failure, e.g. [3,7,14]'),
    graceDays: z.int().min(0),
    readonlyDay: z.int().min(0).describe('day from first failure the account becomes read-only'),
    priceChangeNoticeDays: z.int().min(0),
    active: z.boolean(),
    notes: z.string().nullable(),
  })
  .meta({ id: 'BillingPlan' });
export type BillingPlan = z.infer<typeof BillingPlan>;

// ── Subscription (entity + nested views) ────────────────────────────────────────

export const SubscriptionItem = z
  .object({
    id: Uuid,
    kind: SubscriptionItemKind,
    quantity: z.int().min(0),
    unitPricePence: Pence,
  })
  .meta({ id: 'SubscriptionItem' });
export type SubscriptionItem = z.infer<typeof SubscriptionItem>;

export const Subscription = z
  .object({
    id: Uuid,
    salonId: Uuid,
    planId: Uuid,
    planCode: z.string(),
    status: SubscriptionStatus,
    delinquencyState: SubscriptionDelinquencyState,
    cancelAtPeriodEnd: z.boolean(),
    trialEnd: IsoDateTime.nullable(),
    currentPeriodStart: IsoDateTime.nullable(),
    currentPeriodEnd: IsoDateTime.nullable(),
    canceledAt: IsoDateTime.nullable(),
    items: z.array(SubscriptionItem),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Subscription' });
export type Subscription = z.infer<typeof Subscription>;

// ── Invoices ────────────────────────────────────────────────────────────────────

/** One itemised invoice line — every line traces to logged usage (ADR-0010 §7). */
export const InvoiceLine = z
  .object({
    id: Uuid,
    kind: InvoiceLineKind,
    description: z.string(),
    quantity: z.int().min(0),
    unitPricePence: Pence,
    amountPence: z.int().describe('signed line amount in pence (mirrors Stripe line amounts)'),
    periodStart: IsoDateTime.nullable(),
    periodEnd: IsoDateTime.nullable(),
  })
  .meta({ id: 'InvoiceLine' });
export type InvoiceLine = z.infer<typeof InvoiceLine>;

export const Invoice = z
  .object({
    id: Uuid,
    salonId: Uuid,
    subscriptionId: Uuid.nullable(),
    number: z.string().nullable(),
    status: InvoiceStatus,
    currency: z.literal('GBP'),
    subtotalPence: Pence,
    vatPence: Pence,
    totalPence: Pence,
    amountPaidPence: Pence,
    hostedInvoiceUrl: z.string().nullable(),
    invoicePdfUrl: z.string().nullable(),
    periodStart: IsoDateTime.nullable(),
    periodEnd: IsoDateTime.nullable(),
    issuedAt: IsoDateTime.nullable(),
    paidAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    lines: z.array(InvoiceLine),
  })
  .meta({ id: 'Invoice' });
export type Invoice = z.infer<typeof Invoice>;

// ── Requests ─────────────────────────────────────────────────────────────────

/**
 * Subscribe to a plan. Starts a 14-day trial (plan-configured) with card capture DECOUPLED — no
 * immediate charge (ADR-0011 §1). One-trial-per-salon is enforced server-side: a re-subscribe after a
 * consumed trial resumes PAID, it does not re-trial.
 */
export const CreateSubscription = z
  .object({
    planCode: z.string().describe('the billing_plans.code to subscribe to'),
    /** Optional Stripe PaymentMethod id captured client-side; may be added later before trial end. */
    paymentMethodId: z.string().min(1).max(255).optional(),
    idempotencyKey: z.string().min(8).max(200),
  })
  .meta({ id: 'CreateSubscription' });
export type CreateSubscription = z.infer<typeof CreateSubscription>;

/** Attach / replace the salon's default payment method on the platform Customer (no charge). */
export const SetPaymentMethod = z
  .object({
    paymentMethodId: z.string().min(1).max(255),
  })
  .meta({ id: 'SetPaymentMethod' });
export type SetPaymentMethod = z.infer<typeof SetPaymentMethod>;

/** Change plan — takes effect NEXT cycle (ADR-0011 §2), never mid-cycle. */
export const ChangePlan = z
  .object({
    planCode: z.string(),
  })
  .meta({ id: 'ChangePlan' });
export type ChangePlan = z.infer<typeof ChangePlan>;

/**
 * One-click cancel (ADR-0011 §4): one confirmation, effective end of paid period (cancel_at_period_end).
 * No retention flow. `confirm` must be true — the single confirmation the marketing copy promises.
 */
export const CancelSubscription = z
  .object({
    confirm: z
      .literal(true)
      .describe('the one confirmation; cancel is end-of-period, not immediate'),
  })
  .meta({ id: 'CancelSubscription' });
export type CancelSubscription = z.infer<typeof CancelSubscription>;

// ── Read views ───────────────────────────────────────────────────────────────

/**
 * The per-chair reconciliation view: the derived billable-chair count and the staff list behind it
 * (Overview §6.7 — "the staff list behind the count"). The exact employed/renter/residency predicate
 * is WS11 territory (ADR-0013); this carries the DEFAULT-predicate result.
 */
export const ChairReconciliation = z
  .object({
    billableChairs: z.int().min(0),
    perChairPricePence: Pence,
    staff: z.array(
      z.object({
        staffId: Uuid,
        displayName: z.string(),
      }),
    ),
    /** Set to the new SubscriptionItem quantity when reconcile() applied a change. */
    applied: z.boolean(),
    reconciledAt: IsoDateTime,
  })
  .meta({ id: 'ChairReconciliation' });
export type ChairReconciliation = z.infer<typeof ChairReconciliation>;

/** The named dunning ladder stage (ADR-0011 §3) — the in-app "where am I" view, never a surprise. */
export const DunningStage = z.enum(['none', 'failed', 'retry', 'grace', 'read_only']);
export type DunningStage = z.infer<typeof DunningStage>;

/**
 * The salon's current position on the PUBLISHED dunning ladder (ADR-0011 §3). The schedule (retry days,
 * grace, read-only day) is plan CONFIG — the SAME numbers WS3's terms publish — so the salon always sees
 * exactly when read-only is reached. `stage` is the named ladder position; `delinquencyState` is the
 * derived write-gate state it implies (read_only = new commerce frozen, data still readable/exportable).
 * Always reachable: this is a READ route, exempt from the write-gate (emergency access is unconditional).
 */
export const DunningStatus = z
  .object({
    stage: DunningStage,
    delinquencyState: SubscriptionDelinquencyState,
    /** The first failure that opened the current episode (null when not in dunning). */
    dunningStartedAt: IsoDateTime.nullable(),
    /** Whole days since the first failure (null when not in dunning). */
    daysElapsed: z.int().min(0).nullable(),
    /** Days until the account becomes read-only (0 once reached; null when not in dunning). */
    daysUntilReadOnly: z.int().min(0).nullable(),
    /** The published schedule behind the stage (plan config; the same WS3 terms quote). */
    schedule: z.object({
      retryDays: z.array(z.int().min(0)),
      graceDays: z.int().min(0),
      readonlyDay: z.int().min(0),
    }),
  })
  .meta({ id: 'DunningStatus' });
export type DunningStatus = z.infer<typeof DunningStatus>;

export const ListInvoicesQuery = z.object({
  status: InvoiceStatus.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListInvoicesQuery = z.infer<typeof ListInvoicesQuery>;

// ── Itemised invoice breakdown — the "why is this invoice this amount" trust surface (brief 10 §3) ──

/**
 * The provenance reference behind ONE breakdown line (ADR-0010 §7 — "if we cannot show the receipts,
 * we cannot bill it"). Every non-VAT line MUST carry a `source` other than 'none' so the line traces
 * to logged platform usage; the assembly asserts this invariant. The shape is discriminated by source:
 *
 *   - 'plan'         → the billing_plans row the base price came from (planId/planCode).
 *   - 'staff_chairs' → the derived billable-chair count WITH the staff list behind it (the named
 *                       staff members the chairs×rate line was computed from — Overview §6.7).
 *   - 'comms_audit'  → the per-message audit rows (comms_messages.id) the overage units drill to,
 *                       recomputed by replaying the SAME allowance model the send pipeline used.
 *   - 'none'         → VAT only (VAT is derived from the taxable lines, not from logged usage itself).
 */
export const InvoiceLineProvenance = z
  .object({
    source: z.enum(['plan', 'staff_chairs', 'comms_audit', 'none']),
    /** The billing_plans id/code the base price was read from (source = 'plan'). */
    planId: Uuid.nullable().default(null),
    planCode: z.string().nullable().default(null),
    /** The named staff members behind a chairs×rate line (source = 'staff_chairs'). */
    staff: z.array(z.object({ staffId: Uuid, displayName: z.string() })).default([]),
    /** The audit message ids an overage line drills to (source = 'comms_audit'). */
    messageIds: z.array(Uuid).default([]),
    /** The window the usage was logged in (comms overage / proration), when applicable. */
    periodStart: IsoDateTime.nullable().default(null),
    periodEnd: IsoDateTime.nullable().default(null),
  })
  .meta({ id: 'InvoiceLineProvenance' });
export type InvoiceLineProvenance = z.infer<typeof InvoiceLineProvenance>;

/**
 * One assembled breakdown line: an itemised amount PLUS the provenance reference that lets the console
 * drill it down to logged usage. amountPence here is the ex-VAT (taxable) component for a charge line;
 * the VAT line carries the VAT total for the invoice as a separate, derived line.
 */
export const InvoiceBreakdownLine = z
  .object({
    kind: InvoiceLineKind,
    description: z.string(),
    quantity: z.int().min(0),
    unitPricePence: Pence,
    /** Ex-VAT amount for a charge line; the derived VAT amount for the single `vat` line. */
    amountPence: z.int().describe('signed line amount in pence (ex-VAT for charge lines)'),
    provenance: InvoiceLineProvenance,
  })
  .meta({ id: 'InvoiceBreakdownLine' });
export type InvoiceBreakdownLine = z.infer<typeof InvoiceBreakdownLine>;

/**
 * The full "why is this invoice this amount" view (Overview §6.7; brief 10 §3). Every charge line
 * traces to logged platform usage via `provenance`; Salonomy's OWN UK VAT (vatBps, 20% default,
 * INDEPENDENT of any salon's VAT settings — ADR-0010 §7) is a single derived line on top of the
 * taxable subtotal. INVARIANT (assembly-enforced): subtotalPence (Σ charge lines) + vatPence =
 * totalPence, and every non-VAT line has a provenance source other than 'none'.
 */
export const InvoiceBreakdown = z
  .object({
    invoiceId: Uuid.nullable().describe(
      'null for a pre-issue PREVIEW assembly (no invoice row yet)',
    ),
    salonId: Uuid,
    number: z.string().nullable(),
    status: InvoiceStatus.nullable(),
    currency: z.literal('GBP'),
    periodStart: IsoDateTime.nullable(),
    periodEnd: IsoDateTime.nullable(),
    /** Salonomy's own VAT rate in bps (2000 = 20.00%); unrelated to a salon's VAT settings. */
    vatBps: z.int().min(0).max(10000),
    lines: z.array(InvoiceBreakdownLine),
    subtotalPence: Pence.describe('Σ ex-VAT charge lines (the taxable base)'),
    vatPence: Pence.describe('Salonomy VAT on the subtotal (derived, single line)'),
    totalPence: Pence.describe('subtotalPence + vatPence (VAT-inclusive grand total)'),
  })
  .meta({ id: 'InvoiceBreakdown' });
export type InvoiceBreakdown = z.infer<typeof InvoiceBreakdown>;

// ── Public pricing config — the SINGLE source WS3's pricing page + terms read at build time ──────────

/**
 * A single plan as PUBLIC pricing config (ADR-0010 §2). This is the structural inverse of the internal
 * BillingPlan view: it carries ONLY the customer-facing, publishable fields (price, VAT, allowance, the
 * published lifecycle terms) and OMITS internal plumbing (Stripe price ids, the `active` flag). WS3's
 * pricing page and plain-English terms read this at build time so the published table and the billed
 * amount CANNOT diverge — one source, two readers.
 */
export const PublicPricingPlan = z
  .object({
    code: z.string(),
    displayName: z.string(),
    basePricePence: Pence,
    perChairPricePence: Pence,
    currency: z.literal('GBP'),
    vatBps: z.int().min(0).max(10000).describe("Salonomy's own VAT in bps (2000 = 20.00%)"),
    includedSmsPerChair: z.int().min(0),
    trialPeriodDays: z.int().min(0),
    /** Published dunning ladder (ADR-0011 §3): retry days, grace, read-only day. */
    retryDays: z.array(z.int().min(0)),
    graceDays: z.int().min(0),
    readonlyDay: z.int().min(0),
    /** Advance-notice window for a price change (ADR-0011 §2; published in WS3 terms). */
    priceChangeNoticeDays: z.int().min(0),
  })
  .meta({ id: 'PublicPricingPlan' });
export type PublicPricingPlan = z.infer<typeof PublicPricingPlan>;

/**
 * The public pricing payload WS3 reads at build time. Carries the published comms-overage rate (the 8p
 * published economics) so the pricing page and terms quote the SAME number the biller meters at, and a
 * standing PROPOSAL flag so the page can never present unconfirmed numbers as final (ADR-0010 §2).
 */
export const PublicPricing = z
  .object({
    plans: z.array(PublicPricingPlan),
    /** Published per-unit comms-overage rate in pence (8p default) — the rate the biller meters at. */
    commsOveragePence: Pence,
    /** Standing reminder: the seeded numbers are an ENGINEERING DEFAULT and remain a PROPOSAL. */
    proposal: z.literal(true),
  })
  .meta({ id: 'PublicPricing' });
export type PublicPricing = z.infer<typeof PublicPricing>;
